import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import timezone from 'dayjs/plugin/timezone';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { CarouselJsonLd } from 'next-seo';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import s from 'styles/pages/Cinema.module.css';

import DateSelector from '~components/Billboard/BillboardFilters/DateSelector';
import { getGoogleMapsUrl } from '~components/CinemasDetail/CinemasDetail';
import { CinemaMovie } from '~components/Movies/CinemaMovie';
import EmptyState from '~components/ui/EmptyState/EmptyState';
import Layout from '~components/ui/Layout/Layout';
import { getFromAPI } from '~utils/api/common';
import parseQuery from '~utils/api/parseQuery';
import normalizeCinemaMovie, { NormalizedMovie } from '~utils/normalizers/normalizeCinemaMovie';

import { Cinema, Movie } from '~types';

dayjs.extend(timezone);
dayjs.extend(isToday);

export const getStaticPaths: GetStaticPaths<{ CinemaId: string }> = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

interface GSProps {
  cinema: Cinema;
  initialMovies: NormalizedMovie[];
  initialDates: string[];
}

const slugRegex = /^[a-zA-Z0-9\-_]+$/;
export const getStaticProps: GetStaticProps<GSProps> = async (ctx) => {
  if (!`${ctx?.params?.slug}`.match(slugRegex)) return { notFound: true };

  const cinema = await getFromAPI<Partial<Cinema>>(`/cinemas/${ctx?.params?.slug}`);
  const dates = await getFromAPI(`/shows/dates?CinemaId=${cinema?.id || ''}`);
  const date = dayjs(dates?.[0]?.date)
    .tz(cinema?.timezone || 'America/Hermosillo')
    .format();

  const initialBillboard = await getFromAPI(
    `/shows?${parseQuery({ CinemaId: `${cinema?.id}`, date: date })}`,
  );

  const getMovies = async () => {
    const movies: Movie[] = await Promise.all(
      initialBillboard.map(async ({ id }: { id: string }) => {
        return await getFromAPI<Movie[]>(`/movies/${id}?CinemaId=${cinema?.id || ''}&date=${date}`);
      }),
    );
    return movies.filter((movie) => movie.Shows?.length);
  };

  const movies = await getMovies();

  return {
    props: {
      cinema: cinema as Cinema,
      initialMovies: normalizeCinemaMovie(movies),
      initialDates: dates?.map((item: { date: string }) => item.date),
    },
    revalidate: 5 * 60, // cada 5 min
  };
};

const CinemaPage: NextPage<GSProps> = ({ cinema, initialMovies, initialDates }) => {
  const { query } = useRouter();
  const timezone = cinema?.timezone || 'America/Hermosillo';
  const jsonLdForMovies =
    Array.isArray(initialMovies) &&
    initialMovies.map((movie: NormalizedMovie) => {
      return {
        name: movie.title,
        url: `${process.env.NEXT_PUBLIC_APP_URL}/pelicula/${movie.slug}`,
        image: movie.poster?.thumbnail,
      };
    });

  const setNewPath = (newDate: string) => {
    if (dayjs(newDate).isToday()) {
      return `/cartelera-${cinema?.slug}`;
    }
    return `/cartelera-${cinema?.slug}/${newDate}`;
  };
  return (
    <Layout
      meta={{ title: `Cartelera ${cinema.City.name} Citicinemas ${cinema?.name}` }}
      headerType="opaque"
    >
      <Head>
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_APP_URL}/cartelera-${cinema?.slug}`}
        />
      </Head>
      <CarouselJsonLd ofType="movie" data={jsonLdForMovies} />
      <div className={s.wrapper}>
        <div className={s.header}>
          <h1 className="h2">
            {`${cinema?.name}, ${dayjs((query?.d || query?.date || dayjs()) as string)
              .tz(timezone)
              .format('DD [de] MMMM [de] YYYY')}`}
          </h1>
          <div className={s.services}>
            {cinema?.Services?.map?.((service) => (
              <div key={service?.id} data-tooltip={service?.name}>
                <Image src={service?.icon?.url} alt={service?.name} width={24} height={24} />
              </div>
            ))}
          </div>
          <p>Dirección: {cinema?.address}</p>
          <a
            className="btn btn-secondary btn-sm"
            href={getGoogleMapsUrl(cinema?.name, cinema?.address as string)}
            target="_blank"
            rel="nofollow noreferrer"
          >
            Cómo llegar
          </a>
        </div>
        <div>
          <div className={s.filters}>
            <DateSelector
              date={((query?.d || query?.date) as string) || dayjs().tz(timezone).format()}
              dates={initialDates}
              timezone={timezone}
              getHref={setNewPath}
            />
          </div>
          <div className={s.movies}>
            {!initialMovies?.length && (
              <EmptyState
                type="movie"
                title="No hay películas en cartelera"
                description="En este momento no hay películas disponibles. Intenta más tarde."
                className="py-32"
              />
            )}
            {initialMovies?.map((movie) => (
              <CinemaMovie
                key={movie?.id}
                movie={movie}
                cinemaTimezone={cinema?.timezone as string}
              />
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CinemaPage;
